import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, Stack, Typography,
} from '@mui/material';
import type { FloorPlanDraft } from '../services/floorplan-editor.service';

interface Props {
  readonly draft: FloorPlanDraft;
  readonly busy: boolean;
  readonly onCancelSchedule: () => void;
  readonly onDiscard: () => void;
}

/**
 * Says, while it is true, that what is on screen is a draft: saved, not live, and — when it is —
 * scheduled. Kept in view rather than shown once, because the difference between "I saved it"
 * and "people can book it" is the whole point of a draft.
 */
const DraftBanner = ({
  draft, busy, onCancelSchedule, onDiscard,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const when = (value: string): string => new Date(value).toLocaleString();

  return (
    <Stack spacing={0.5} sx={{ m: 1 }}>
      <Alert
        severity="info"
        sx={{ py: 0, alignItems: 'center' }}
        action={(
          <Stack direction="row" spacing={1}>
            {draft.publishAt && (
              <Button size="small" onClick={onCancelSchedule} disabled={busy} sx={{ textTransform: 'none' }}>
                {t('floorplan-editor-draft-cancel-schedule')}
              </Button>
            )}
            <Button size="small" color="error" onClick={onDiscard} disabled={busy} sx={{ textTransform: 'none' }}>
              {t('floorplan-editor-draft-discard')}
            </Button>
          </Stack>
        )}
      >
        <Typography variant="body2">
          {t('floorplan-editor-draft-banner', { when: when(draft.lastUpdatedAt), who: draft.authorEmail ?? '—' })}
          {draft.publishAt ? ` ${String(t('floorplan-editor-draft-banner-scheduled', { when: when(draft.publishAt) }))}` : ''}
        </Typography>
      </Alert>

      {draft.lastPublishError && (
        <Alert severity="error" sx={{ py: 0 }}>
          {t('floorplan-editor-draft-publish-failed', { reason: draft.lastPublishError })}
        </Alert>
      )}

      {draft.liveChangedSince && (
        <Alert severity="warning" sx={{ py: 0 }}>{t('floorplan-editor-draft-live-changed')}</Alert>
      )}
    </Stack>
  );
};

export default DraftBanner;
